import { Injectable } from "@angular/core";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { DealerBrandingDbService } from "@app/entity/services/dealer-branding-db.service";
import { emptyAction } from "@app/root-store/actions/snackbar.action";
import { EffectsUtils } from "@app/root-store/effects/effect-creators";
import { ActionWord } from "@app/shared/resources/action-word";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, catchError, exhaustMap, map, of, switchMap, tap } from "rxjs";
import { DealerBrandingActions } from "../actions";
import { DealerBrandingActionPayload, DeleteDealerBrandingImageActions } from "../actions/dealer-branding.actions";
import { DeleteConfirmationService } from "@app/shared/components/delete-confirmation/delete-confirmation.service";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { DealerBrandingService } from "@app/entity/services/dealer-branding.service";

@Injectable()
export class DealerBrandingEffects extends EffectsUtils {
    constructor(
        actions$: Actions,
        private readonly dealerBrandingDbService: DealerBrandingDbService,
        private readonly auditLogService: AuditLogService,
        private readonly deleteConfirmationService: DeleteConfirmationService,
        private readonly dealerBrandingService: DealerBrandingService
    ) {
        super(actions$);
    }

    getDealerBrandingImage$ = this.createSwitchEffect({
        dbMethod: ({ key }) => this.dealerBrandingDbService.loadImageData({ key }),
        actionSet: DealerBrandingActions.loadSingle,
    });
    getDealerBrandingImageFailure$ = this.createFailureEffect(DealerBrandingActions.loadSingle.failure, ActionWord.loaded);

    saveDealerBrandingImage$ = this.createExhaustEffect({
        dbMethod: (payload: DealerBrandingActions.ActionPayload) =>
            this.dealerBrandingDbService.saveImageData(payload.image).pipe(this.logEvent(payload)),
        actionSet: DealerBrandingActions.createSet,
    });
    saveDealerBrandingImageSuccess$ = this.createSuccessEffect(DealerBrandingActions.createSet.success, ActionWord.saved);
    saveDealerBrandingImageFailure$ = this.createFailureEffect(DealerBrandingActions.createSet.failure, ActionWord.saved);

    getZipFile$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.ActionTypes.DealerBrandingZipFileDownload),
            switchMap((action: DealerBrandingActions.GetDealerBrandingZipFileDownload) =>
                this.dealerBrandingDbService.getZipFile(action.payload.zipData).pipe(
                    tap((zipFile: File) => {
                        action.payload.callback.emit(zipFile);
                    })
                )
            ),
            map(res => emptyAction())
        )
    );

    getEncodedZipFile$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.ActionTypes.GetEncodedDealerBrandingZipFile),
            switchMap((action: DealerBrandingActions.GetEncodedDealerBrandingZipFile) =>
                this.dealerBrandingDbService.getEncodedZipFile(action.payload.zipData).pipe(
                    tap((zipFile: File) => {
                        action.payload.callback.emit(zipFile);
                    })
                )
            ),
            map(res => emptyAction())
        )
    );

    createDealerBrandingImage$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.CreateDealerBrandingImageActions.request),
            switchMap((payload: DealerBrandingActionPayload) =>
                this.dealerBrandingDbService.createDealerBrandingImage$(payload).pipe(
                    tap(_ => this.dealerBrandingService.dispatchAction(DealerBrandingActions.clearDealerBrandingImageListState())),
                    this.logEvent(payload),
                    map(_ => DealerBrandingActions.CreateDealerBrandingImageActions.success(null)),
                    catchError(error => of(DealerBrandingActions.CreateDealerBrandingImageActions.failure({ error })))
                )
            )
        )
    );

    loadDealerBrandingImageList$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.LoadDealerBrandingImageListActions.request),
            switchMap((payload: DealerBrandingActionPayload) =>
                this.dealerBrandingDbService.loadDealerBrandingImageList$(payload.entityId).pipe(
                    map(images => DealerBrandingActions.LoadDealerBrandingImageListActions.success({ images: images || [] })),
                    catchError(error => of(DealerBrandingActions.LoadDealerBrandingImageListActions.failure({ error })))
                )
            )
        )
    );

    loadDealerBrandingImageByFileName$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.LoadDealerBrandingImageActions.request),
            switchMap((payload: DealerBrandingActionPayload) =>
                this.dealerBrandingDbService.loadDealerBrandingImageByFileName$(payload).pipe(
                    map(dealerBranding => DealerBrandingActions.LoadDealerBrandingImageActions.success({ dealerBranding })),
                    catchError(error => of(DealerBrandingActions.LoadDealerBrandingImageActions.failure({ error })))
                )
            )
        )
    );

    deleteDealerBrandingImage$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.DeleteDealerBrandingImageActions.request),
            switchMap((payload: DealerBrandingActionPayload) =>
                this.dealerBrandingDbService.deleteDealerBrandingImage$(payload).pipe(
                    this.logEvent(payload),
                    map(_ => DealerBrandingActions.DeleteDealerBrandingImageActions.success({ key: payload.key })),
                    catchError(error => of(DealerBrandingActions.DeleteDealerBrandingImageActions.failure({ error })))
                )
            )
        )
    );

    deleteConfirmation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DealerBrandingActions.deleteDealerBrandingImageConfirmation),
            exhaustMap((payload: DealerBrandingActionPayload) =>
                this.deleteConfirmationService.open$<DealerBrandingActionPayload>(
                    payload,
                    DealerBrandingActions.deleteDealerBrandingImageConfirmationDismiss,
                    DeleteDealerBrandingImageActions.request,
                    payload.fileName
                )
            )
        )
    );

    logEvent = (payload: { logData?: AuditLogInput }) =>
        tap(() => {
            if (payload.logData) {
                const { auditName, ...log } = payload.logData;
                this.auditLogService.logEvent(auditName, log);
            }
        });
}
